/**
 * LeaderCoordinator — elects ONE persistence leader per (origin, siteId), and
 * prefers the NEWEST app version so the freshest code owns OPFS + any schema
 * migration. Multiple tabs can be open (cross-tab app); only the leader touches
 * OPFS (see useEditorDoc), everyone else is a follower syncing over
 * BroadcastChannel.
 *
 * Election: the Web Locks API. Whoever holds the exclusive lock is the leader;
 * when it releases (tab close OR a voluntary yield) a waiting tab takes over.
 *
 * Version-recency: tabs announce their version on a side channel. A leader that
 * hears a STRICTLY-NEWER peer YIELDS (releases the lock) so the newer tab —
 * which is already queued for the lock — becomes leader. Newest wins, and
 * equals never churn (total order). The two announces (on start, and on
 * assuming leadership) plus a follower re-announce close the join-order race in
 * both directions.
 */

export interface AppVersion {
  /** Dotted numeric app version (package.json). */
  readonly semver: string;
  /** Build timestamp (epoch ms) — breaks semver ties across rebuilds. */
  readonly build: number;
}

/** Total order, newest-greatest: compare semver numerically, tie-break by build.
 *  Returns <0 if a older, 0 if equal, >0 if a newer. */
export function compareVersion(a: AppVersion, b: AppVersion): number {
  const pa = a.semver.split(".");
  const pb = b.semver.split(".");
  const n = Math.max(pa.length, pb.length);
  for (let i = 0; i < n; i++) {
    const d =
      (parseInt(pa[i] ?? "0", 10) || 0) - (parseInt(pb[i] ?? "0", 10) || 0);
    if (d !== 0) return d < 0 ? -1 : 1;
  }
  const d = a.build - b.build;
  return d === 0 ? 0 : d < 0 ? -1 : 1;
}

export interface LeaderCallbacks {
  /** Assume leadership: open OPFS + start persisting. Awaited. */
  onLead: () => Promise<void>;
  /** Give up leadership: close OPFS. Awaited BEFORE the lock is released, so the
   *  next leader never opens OPFS while this tab still holds it. */
  onResign: () => Promise<void>;
}

type Announce = { t: "announce"; v: AppVersion; leader: boolean };

/** How long the pre-open ping waits for a strictly-newer peer to prompt a yield
 *  before opening OPFS (F4). Long enough for a BroadcastChannel round-trip,
 *  short enough not to noticeably delay leadership; only paid when a newer peer
 *  was previously heard (never in the common single-tab / newest-tab case). */
const NEWER_PEER_PING_MS = 60;

export class LeaderCoordinator {
  private chan: BroadcastChannel;
  private lockName: string;
  private destroy$ = new AbortController();
  /** Aborts the CURRENT leadership term (a yield); distinct from destroy$. */
  private term: AbortController | null = null;
  private isLeader = false;
  private started = false;
  /** Highest version heard from any peer — MEMORY that triggers the pre-open
   *  ping (F4). It is NOT treated as authority: a newer tab that has since
   *  CLOSED must not block us, so the ping re-confirms liveness before yielding. */
  private newestSeen: AppVersion | null = null;
  /** True when Web Locks is unavailable → best-effort single owner (no queue,
   *  so no re-lead; it can only resign to a newer version, F7). */
  private noLocks = false;

  constructor(
    siteId: string,
    private version: AppVersion,
    private cbs: LeaderCallbacks,
  ) {
    this.lockName = `wb-persist:${siteId}`;
    this.chan = new BroadcastChannel(`wb-lead:${siteId}`);
    this.chan.onmessage = this.onMessage;
  }

  start(): void {
    if (this.started) return;
    this.started = true;
    // Announce ourselves so an already-established OLDER leader steps down.
    this.announce(false);

    const locks = getLocks();
    if (!locks) {
      // No Web Locks → best-effort single owner. No lock queue, so it can't
      // re-lead; it CAN still step down to a strictly-newer version (F7, in
      // onMessage) so an older build stops persisting once a newer tab is open.
      this.noLocks = true;
      this.isLeader = true;
      void this.cbs.onLead().catch(noop);
      return;
    }
    // Try to lead immediately; otherwise queue for handover.
    void locks
      .request(this.lockName, { ifAvailable: true }, async (lock) => {
        if (lock) await this.hold();
        else await this.waitToLead();
      })
      .catch(noop);
  }

  /** Block until we acquire the lock (the current leader closed or yielded). */
  private async waitToLead(): Promise<void> {
    const locks = getLocks();
    if (!locks) return;
    try {
      await locks.request(
        this.lockName,
        { signal: this.destroy$.signal },
        async () => {
          await this.hold();
        },
      );
    } catch {
      /* AbortError on destroy — expected */
    }
  }

  /** Hold the lock as leader until a yield or teardown; open/close OPFS around
   *  it. On a yield (not teardown) we re-queue to lead again later. */
  private async hold(): Promise<void> {
    if (this.destroy$.signal.aborted) return;
    this.term = new AbortController();
    const term = this.term;
    // Claim leadership up front so onMessage will yield us to a strictly-newer
    // peer even during the pre-open ping below (before OPFS is open).
    this.isLeader = true;

    // F4 — pre-open ping. If we've heard a strictly-newer peer, announce as
    // leader and give it a brief window to prompt a yield BEFORE opening OPFS.
    // A live newer peer re-announces on hearing our leader claim (onMessage),
    // aborting this term; a newer peer that has since CLOSED never answers, so
    // we fall through and lead. Skipped entirely when no newer peer was heard —
    // the common single-tab / newest-tab case pays no delay. Worst case (the
    // ping misses) is the original behaviour: open, then yield on the next
    // announce — bounded, and clean because onResign closes OPFS before release.
    if (this.newestSeen && compareVersion(this.newestSeen, this.version) > 0) {
      this.announce(true);
      await abortableDelay(
        NEWER_PEER_PING_MS,
        this.destroy$.signal,
        term.signal,
      );
      if (this.destroy$.signal.aborted) {
        this.isLeader = false;
        this.term = null;
        return;
      }
      if (term.signal.aborted) {
        // A newer peer prompted a yield before we touched OPFS.
        this.isLeader = false;
        this.term = null;
        void this.waitToLead();
        return;
      }
    }

    await this.cbs.onLead().catch(noop);
    // Tell peers who leads + at what version (so a newer follower can prompt a
    // yield — the reverse of the start() announce).
    this.announce(true);

    await abortRace(this.destroy$.signal, term.signal);

    this.isLeader = false;
    await this.cbs.onResign().catch(noop); // close OPFS BEFORE releasing the lock
    this.term = null;
    // If we YIELDED (term aborted, not destroyed), get back in line to lead.
    if (term.signal.aborted && !this.destroy$.signal.aborted) {
      void this.waitToLead();
    }
    // Returning here releases the lock → the next queued tab leads.
  }

  private announce(leader: boolean): void {
    const msg: Announce = { t: "announce", v: this.version, leader };
    try {
      this.chan.postMessage(msg);
    } catch {
      /* channel closing */
    }
  }

  private onMessage = (e: MessageEvent<Announce>): void => {
    const m = e.data;
    if (!m || m.t !== "announce") return;
    // Remember the newest version ever heard — triggers the pre-open ping (F4).
    if (
      compareVersion(m.v, this.version) > 0 &&
      (!this.newestSeen || compareVersion(m.v, this.newestSeen) > 0)
    ) {
      this.newestSeen = m.v;
    }
    if (m.leader) {
      // A leader announced. If I'm a newer follower, re-announce so it yields.
      if (!this.isLeader && compareVersion(this.version, m.v) > 0) {
        this.announce(false);
      }
    } else {
      // A follower announced. If I'm the leader and it's newer, step down.
      if (this.isLeader && compareVersion(m.v, this.version) > 0) {
        if (this.term) {
          this.term.abort(); // Web Locks path: yield + re-queue to lead later
        } else if (this.noLocks) {
          void this.resignNoLocks(); // best-effort: close OPFS, no re-lead
        }
      }
    }
  };

  /** Step down in the no-Web-Locks path: there's no lock queue to re-lead from,
   *  so we simply close OPFS and stay down. The newer tab (also a single owner
   *  in its own no-locks context) then owns persistence best-effort. */
  private async resignNoLocks(): Promise<void> {
    if (!this.isLeader) return;
    this.isLeader = false;
    await this.cbs.onResign().catch(noop);
  }

  destroy(): void {
    this.destroy$.abort(); // release the lock / cancel a pending handover request
    this.term?.abort();
    try {
      this.chan.close();
    } catch {
      /* already closed */
    }
  }
}

function getLocks(): LockManager | undefined {
  return typeof navigator !== "undefined"
    ? (navigator.locks as LockManager | undefined)
    : undefined;
}

/** Resolve when either signal aborts. */
function abortRace(a: AbortSignal, b: AbortSignal): Promise<void> {
  return new Promise<void>((resolve) => {
    if (a.aborted || b.aborted) return resolve();
    const done = (): void => resolve();
    a.addEventListener("abort", done, { once: true });
    b.addEventListener("abort", done, { once: true });
  });
}

/** Resolve after `ms`, or earlier if either signal aborts. Clears the timer on
 *  an early abort so nothing lingers. */
function abortableDelay(
  ms: number,
  a: AbortSignal,
  b: AbortSignal,
): Promise<void> {
  return new Promise<void>((resolve) => {
    if (a.aborted || b.aborted) return resolve();
    const timer = setTimeout(finish, ms);
    function finish(): void {
      clearTimeout(timer);
      a.removeEventListener("abort", finish);
      b.removeEventListener("abort", finish);
      resolve();
    }
    a.addEventListener("abort", finish, { once: true });
    b.addEventListener("abort", finish, { once: true });
  });
}

function noop(): void {
  /* swallow */
}
